import {
  adviserPoolSchema,
  adviserHoursSchema,
  timezoneSchema,
} from './advisers';
import { responseModelSettingsSchema } from './models';
import { appointmentTimeWindowSchema } from './appointment-time-window';
import { z } from 'zod';
import {
  salesPlaybookSchema,
  buyerSalesSignalsSchema,
  buyerContextPatchSchema,
  salesTurnPlanSchema,
  salesTechniqueIdSchema,
} from './sales-psychology';
import {
  chatPresentationSchema,
  apartmentReferenceSchema,
  resolvedApartmentSchema,
} from './chat-presentation';
import { botScopeSchema, uuidV7Schema } from './ids';
import { positiveMoneyMinorStringSchema } from './money';
import { propertySearchSchema } from './catalog';
import { catalogProfile } from './catalog-profiles';
import {
  catalogConfigurationSchema,
  catalogQuerySchema,
} from './catalog-contracts';
import { salesTurnUnderstandingSchema } from './sales-turn';
import { appointmentOfferSchema } from './appointment-selection';

export const channelKindSchema = z.enum(['chat', 'email']);
export type ChannelKind = z.infer<typeof channelKindSchema>;
export const officeProviderSchema = z.enum(['google', 'microsoft']);
export type OfficeProvider = z.infer<typeof officeProviderSchema>;
// The provider refused the stored grant: only connecting the account again
// restores it; retrying or re-enabling the connection cannot succeed.
export const OFFICE_RECONNECTION_REQUIRED_CODES: readonly string[] = [
  'OFFICE_REAUTHORIZATION_REQUIRED',
  'OFFICE_OAUTH_CLIENT_REJECTED',
  'OFFICE_REQUIRED_PERMISSIONS_MISSING',
];
export const capabilitySchema = z.enum(['offerings', 'sales', 'collections']);
export const commercialOutcomeSchema = z.enum([
  'qualified_lead',
  'appointment',
]);
export type CommercialOutcome = z.infer<typeof commercialOutcomeSchema>;
export const timestampSchema = z.iso.datetime({ offset: true });
const boundedText = z.string().trim().min(1).max(16_000);
export const emailAddressSchema = z.email().max(254).toLowerCase();

export const communicationProfileSchema = z
  .object({
    assistantName: z.string().trim().min(1).max(100),
    businessName: z.string().trim().min(1).max(200),
    locale: z.enum(['es-PE', 'en-US']),
    formality: z.enum(['formal', 'conversational']),
    personality: z.string().trim().min(1).max(800).optional(),
    instructions: z.string().trim().max(4_000),
    signature: z.string().trim().max(500),
  })
  .strict();
const calendarTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u);
const calendarDaySchema = z.number().int().min(0).max(6);
export const calendarWorkingHoursSchema = z
  .object({
    day: calendarDaySchema,
    opensAt: calendarTimeSchema,
    closesAt: calendarTimeSchema,
  })
  .strict()
  .refine((hours) => hours.opensAt < hours.closesAt, {
    message: 'Calendar closing time must follow opening time.',
  });
export type CalendarWorkingHours = z.infer<typeof calendarWorkingHoursSchema>;
const calendarIdentityShape = {
  serviceName: z.string().trim().min(1).max(120).optional(),
  location: z.string().trim().min(1).max(500).optional(),
  connectionId: uuidV7Schema,
  calendarId: z.string().min(1).max(500),
  timezone: z.string().min(1).max(100),
  durationMinutes: z.number().int().min(5).max(240),
  appointmentKind: z
    .enum(['appointment', 'site_visit', 'sales_call'])
    .optional(),
};
export const providerCalendarConfigurationSchema = z.union([
  z
    .object({
      ...calendarIdentityShape,
      weeklyHours: adviserHoursSchema.refine(
        (hours) => hours.length > 0,
        'Working hours are required.'
      ),
    })
    .strict(),
  // Immutable releases with a shared daily window remain readable.
  z
    .object({
      ...calendarIdentityShape,
      workingDays: z.array(calendarDaySchema).min(1).max(7),
      opensAt: calendarTimeSchema,
      closesAt: calendarTimeSchema,
    })
    .strict()
    .refine(
      (hours) => hours.opensAt < hours.closesAt,
      'Calendar closing time must follow opening time.'
    ),
]);
export const pooledCalendarConfigurationSchema = z
  .object({
    pool: adviserPoolSchema,
    serviceName: z.string().trim().min(1).max(120).optional(),
    location: z.string().trim().min(1).max(500).optional(),
    timezone: timezoneSchema,
    durationMinutes: z.number().int().min(5).max(240),
    appointmentKind: z
      .enum(['appointment', 'site_visit', 'sales_call'])
      .optional(),
    weeklyHours: adviserHoursSchema.refine(
      (hours) => hours.length > 0,
      'Service working hours are required.'
    ),
  })
  .strict();
export const calendarConfigurationSchema = z.union([
  providerCalendarConfigurationSchema,
  pooledCalendarConfigurationSchema,
]);
export type ProviderCalendarConfiguration = z.infer<
  typeof providerCalendarConfigurationSchema
>;
export type PooledCalendarConfiguration = z.infer<
  typeof pooledCalendarConfigurationSchema
>;
export const calendarWorkingHours = (
  calendar: z.infer<typeof calendarConfigurationSchema>
): CalendarWorkingHours[] =>
  'weeklyHours' in calendar
    ? calendar.weeklyHours
    : calendar.workingDays.map((day) => ({
        day,
        opensAt: calendar.opensAt,
        closesAt: calendar.closesAt,
      }));
export const calendarAppointmentKind = (
  calendar: z.infer<typeof calendarConfigurationSchema>
): 'appointment' | 'site_visit' | 'sales_call' =>
  calendar.appointmentKind ?? 'appointment';

export const appointmentServiceSchema = z
  .object({
    id: uuidV7Schema,
    name: z.string().trim().min(1).max(120),
    location: z.string().trim().max(500),
    calendar: calendarConfigurationSchema,
  })
  .strict()
  .refine(
    (service) =>
      calendarAppointmentKind(service.calendar) !== 'site_visit' ||
      service.location.length > 0,
    'A visit needs its location.'
  );
export const assistantConfigurationSchema = z
  .object({
    profile: communicationProfileSchema,
    responseModel: responseModelSettingsSchema.optional(),
    business: z
      .object({
        description: z.string().trim().min(1).max(2000),
        audience: z.string().trim().min(1).max(2000),
      })
      .strict()
      .optional(),
    salesPlaybook: salesPlaybookSchema.optional(),
    commercialOutcomes: z
      .array(commercialOutcomeSchema)
      .max(2)
      .refine(
        (outcomes) => new Set(outcomes).size === outcomes.length,
        'Commercial outcomes must be unique.'
      )
      .optional(),
    leadDestination: z.enum(['assist', 'business']).optional(),
    appointmentServices: z
      .array(appointmentServiceSchema)
      .max(20)
      .refine(
        (services) =>
          new Set(services.map((service) => service.id)).size ===
          services.length,
        'Appointment service IDs must be unique.'
      )
      .optional(),
    capabilities: z.array(capabilitySchema).min(1).max(3),
    channels: z
      .object({
        chat: z
          .object({
            enabled: z.boolean(),
            presentation: chatPresentationSchema.optional(),
            maxCatalogCards: z.number().int().min(1).max(12).optional(),
            allowedOrigins: z.array(z.url({ protocol: /^https$/u })).max(20),
          })
          .strict(),
        email: z
          .object({
            enabled: z.boolean(),
            mode: z.enum(['draft', 'automatic']),
          })
          .strict(),
      })
      .strict(),
    calendar: calendarConfigurationSchema.nullable(),
    businessConnectionId: uuidV7Schema.nullable(),
    catalog: catalogConfigurationSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.catalog) {
      if (
        !value.businessConnectionId ||
        !value.capabilities.includes('offerings')
      )
        context.addIssue({
          code: 'custom',
          path: ['catalog'],
          message: 'Catalog settings require an enabled, connected catalog.',
        });
      try {
        catalogProfile(value.catalog.profile, value.catalog);
      } catch (error) {
        context.addIssue({
          code: 'custom',
          path: ['catalog'],
          message:
            error instanceof Error ? error.message : 'Invalid catalog profile.',
        });
      }
    }
    if (!value.channels.chat.enabled && !value.channels.email.enabled) {
      context.addIssue({
        code: 'custom',
        message: 'At least one channel must be enabled.',
      });
    }
    if (
      value.channels.chat.enabled &&
      value.channels.chat.allowedOrigins.length === 0
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Chat requires an authorized website origin.',
      });
    }
    for (const origin of value.channels.chat.allowedOrigins) {
      if (new URL(origin).origin !== origin)
        context.addIssue({
          code: 'custom',
          message: 'Chat origins must not contain paths.',
        });
    }
    if (value.calendar && value.appointmentServices !== undefined)
      context.addIssue({
        code: 'custom',
        message:
          'Choose named appointment services or a single calendar, not both.',
      });
    if (
      (value.calendar || value.appointmentServices?.length) &&
      !value.capabilities.includes('sales')
    )
      context.addIssue({
        code: 'custom',
        message: 'Appointment services require sales assistance.',
      });
    const commercialOutcomes =
      value.commercialOutcomes ??
      (value.capabilities.includes('sales')
        ? [
            'qualified_lead' as const,
            ...(value.calendar || value.appointmentServices?.length
              ? (['appointment'] as const)
              : []),
          ]
        : []);
    if (commercialOutcomes.length > 0 && !value.capabilities.includes('sales'))
      context.addIssue({
        code: 'custom',
        message: 'Commercial outcomes require sales assistance.',
      });
    if (
      commercialOutcomes.includes('appointment') &&
      !value.calendar &&
      !value.appointmentServices?.length
    )
      context.addIssue({
        code: 'custom',
        message: 'The appointment outcome requires an appointment service.',
      });
    if (
      (value.calendar || value.appointmentServices?.length) &&
      !commercialOutcomes.includes('appointment')
    )
      context.addIssue({
        code: 'custom',
        message:
          'Configured appointment services require the appointment outcome.',
      });
    if (value.leadDestination === 'business' && !value.businessConnectionId) {
      context.addIssue({
        code: 'custom',
        message: 'A business lead destination requires a connection.',
      });
    }
    for (const calendar of [
      ...(value.calendar ? [value.calendar] : []),
      ...(value.appointmentServices?.map((service) => service.calendar) ?? []),
    ]) {
      try {
        new Intl.DateTimeFormat('en', { timeZone: calendar.timezone });
      } catch {
        context.addIssue({
          code: 'custom',
          message: 'Calendar timezone is invalid.',
        });
      }
    }
    if (
      value.capabilities.includes('collections') &&
      !value.businessConnectionId
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Collections requires an authoritative business connection.',
      });
    }
  });
export type AssistantConfiguration = z.infer<
  typeof assistantConfigurationSchema
>;

export function configuredCommercialOutcomes(
  configuration: AssistantConfiguration
): CommercialOutcome[] {
  if (configuration.commercialOutcomes)
    return [...configuration.commercialOutcomes];
  if (!configuration.capabilities.includes('sales')) return [];
  return [
    'qualified_lead',
    ...(configuration.calendar || configuration.appointmentServices?.length
      ? (['appointment'] as const)
      : []),
  ];
}

export interface ChannelCapabilities {
  streaming: boolean;
  attachments: boolean;
  threads: boolean;
  structuredChoices: boolean;
  deliveryReceipts: boolean;
}

export const attachmentReferenceSchema = z
  .object({
    id: uuidV7Schema,
    filename: z.string().min(1).max(250),
    contentType: z.string().min(1).max(150),
    bytes: z.number().int().positive().max(10_000_000),
  })
  .strict();
export type AttachmentReference = z.infer<typeof attachmentReferenceSchema>;

export const inboundChannelEventSchema = z
  .object({
    scope: botScopeSchema,
    connectionId: uuidV7Schema,
    channel: channelKindSchema,
    sourceId: z.string().min(1).max(500),
    threadId: z.string().min(1).max(500),
    occurredAt: timestampSchema,
    sender: z.string().min(1).max(500),
    text: z.string().max(32_000),
    subject: z.string().max(500).nullable(),
    attachments: z.array(attachmentReferenceSchema).max(10),
    kind: z.enum([
      'customer_message',
      'operator_message',
      'delivery_status',
      'automatic_message',
    ]),
  })
  .strict();
export type InboundChannelEvent = z.infer<typeof inboundChannelEventSchema>;

export const inboundEnvelopeSchema = z
  .object({
    event: inboundChannelEventSchema,
    apartment: resolvedApartmentSchema.optional(),
    originalText: z.string().max(128_000),
    email: z
      .object({
        internetMessageId: z.string().min(1).max(1_000),
        references: z.array(z.string().max(1_000)).max(100),
        replyTo: emailAddressSchema.nullable(),
        recipients: z.array(emailAddressSchema).max(100),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type InboundEnvelope = z.infer<typeof inboundEnvelopeSchema>;

export const outgoingContentSchema = z
  .object({
    recipient: z.string().min(1).max(500),
    subject: z.string().max(500).nullable(),
    text: z.string().min(1).max(16_000),
    replyToProviderMessageId: z.string().max(500).nullable(),
    providerThreadId: z.string().max(500).nullable(),
    internetMessageId: z.string().max(1_000).nullable(),
    references: z.array(z.string().max(1_000)).max(100),
    attachmentIds: z.array(uuidV7Schema).max(10),
  })
  .strict();
export type OutgoingContent = z.infer<typeof outgoingContentSchema>;

export const channelDeliveryResultSchema = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('accepted'),
      providerMessageId: z.string().min(1),
      providerThreadId: z.string().nullable(),
      acceptedAt: timestampSchema,
    })
    .strict(),
  z
    .object({
      status: z.literal('reconciliation_required'),
      operationReference: z.string().min(1),
      code: z.string().min(1),
    })
    .strict(),
  z
    .object({
      status: z.literal('failed'),
      code: z.string().min(1),
      retryable: z.boolean(),
    })
    .strict(),
]);
export type ChannelDeliveryResult = z.infer<typeof channelDeliveryResultSchema>;

export const customerMessageSchema = z
  .object({
    requestId: uuidV7Schema,
    apartment: apartmentReferenceSchema.optional(),
    text: boundedText,
    attachments: z.array(uuidV7Schema).max(10),
  })
  .strict();

export const salesSignalSchema = z.enum([
  'factual_question',
  'exploration',
  'comparison',
  'objection',
  'appointment_request',
  'appointment_confirmation',
  'appointment_refusal',
  'other',
]);
export type SalesSignal = z.infer<typeof salesSignalSchema>;

export const decisionSchema = z
  .object({
    intent: z.enum([
      'offering_question',
      'sales',
      'appointment',
      'account_question',
      'payment_claim',
      'dispute',
      'payment_proposal',
      'human_help',
      'stop',
      'other',
    ]),
    responseLanguage: z.enum(['en', 'es', 'other']).optional(),
    salesSignal: salesSignalSchema.optional(),
    contactOnly: z.boolean().optional(),
    appointmentAuthorized: z.boolean().optional(),
    catalogBrowse: z.enum(['apartments', 'all']).optional(),
    priceOverviewRequested: z.boolean().optional(),
    availabilityOverviewRequested: z.boolean().optional(),
    catalogSearchRequested: z.boolean().optional(),
    catalogPriceOrder: z.enum(['none', 'ascending', 'descending']).optional(),
    catalogInventoryScope: z.enum(['project', 'placement', 'mixed']).optional(),
    salesContinuation: z.enum(['proceed', 'pause']).optional(),
    appointmentOperation: z
      .enum(['none', 'availability', 'book', 'reschedule', 'cancel', 'status'])
      .optional(),
    salesSignals: buyerSalesSignalsSchema.optional(),
    memoryPlanId: uuidV7Schema.nullable().optional(),
    paymentClaim: z.boolean(),
    dispute: z.boolean(),
    stop: z.boolean(),
    needsHuman: z.boolean(),
    handling: z
      .object({
        reason: z.enum([
          'ordinary',
          'sensitive_request',
          'sensitive_uncertainty',
        ]),
        uncertainSignals: z
          .array(z.enum(['paymentClaim', 'dispute', 'stop', 'needsHuman']))
          .max(4),
        restrictSensitiveActions: z.boolean(),
        humanConfidence: z.number().min(0).max(1),
        humanProbability: z.number().min(0).max(1),
      })
      .strict()
      .optional(),
    evidence: z.array(z.string().min(1).max(2_000)).max(10),
  })
  .strict();
export type ConversationDecision = z.infer<typeof decisionSchema>;

export const sourceFactSchema = z
  .object({
    id: z.string().min(1).max(500),
    source: z.string().min(1).max(2_000),
    version: z.string().min(1).max(200),
    observedAt: timestampSchema,
    content: boundedText,
    private: z.boolean(),
  })
  .strict();
export type SourceFact = z.infer<typeof sourceFactSchema>;

export const businessActionSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('search_offerings'),
      query: boundedText,
      propertySearch: propertySearchSchema.nullable().default(null),
      searchScope: z.enum(['preferences', 'question']).optional(),
      catalog: catalogQuerySchema.optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('create_lead'),
      firstName: z.string().trim().min(1).max(100),
      lastName: z.string().trim().min(1).max(100),
      phone: z
        .string()
        .regex(/^(?:9(?:[\s().-]*\d){8}|\+[1-9](?:[\s().-]*\d){6,14})$/u),
      email: emailAddressSchema,
      request: boundedText,
    })
    .strict(),
  z
    .object({
      kind: z.literal('calendar_availability'),
      serviceId: uuidV7Schema.nullable().optional(),
      startsAt: timestampSchema,
      endsAt: timestampSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('find_appointment_slots'),
      serviceId: uuidV7Schema.nullable().optional(),
      localDate: z.iso.date().nullable(),
      weekday: calendarDaySchema.nullable(),
      timeOfDay: z.enum(['any', 'morning', 'afternoon']),
      timeWindow: appointmentTimeWindowSchema.nullable().optional(),
      localTime: z
        .string()
        .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u)
        .nullable()
        .default(null),
      offset: z.number().int().min(0).max(300),
    })
    .strict()
    .refine((value) => value.localDate === null || value.weekday === null, {
      message: 'Choose either a local date or a weekday.',
    }),
  z
    .object({
      kind: z.literal('book_appointment'),
      serviceId: uuidV7Schema.nullable().optional(),
      startsAt: timestampSchema,
      endsAt: timestampSchema,
      name: z.string().min(1).max(200),
      email: emailAddressSchema,
      subject: z.string().min(1).max(200),
    })
    .strict(),
  z
    .object({
      kind: z.literal('request_sales_appointment'),
      serviceId: uuidV7Schema.nullable().optional(),
      firstName: z.string().trim().min(1).max(100),
      lastName: z.string().trim().min(1).max(100),
      phone: z
        .string()
        .regex(/^(?:9(?:[\s().-]*\d){8}|\+[1-9](?:[\s().-]*\d){6,14})$/u),
      email: emailAddressSchema,
      request: boundedText,
      startsAt: timestampSchema,
      endsAt: timestampSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('list_appointments'),
      before: uuidV7Schema.nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('cancel_appointment'),
      appointmentActionId: uuidV7Schema,
      expectedVersion: z.string().min(1).max(1000),
      reason: boundedText,
    })
    .strict(),
  z
    .object({
      kind: z.literal('reschedule_appointment'),
      appointmentActionId: uuidV7Schema,
      expectedVersion: z.string().min(1).max(1000),
      startsAt: timestampSchema,
      endsAt: timestampSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('read_receivables'),
      reference: z.string().min(1).max(200).nullable().default(null),
      cursor: z.string().min(1).max(200).nullable().default(null),
    })
    .strict(),
  z.object({ kind: z.literal('verify_account') }).strict(),
  z
    .object({
      kind: z.literal('payment_claim'),
      reference: z.string().min(1).max(500),
      explanation: boundedText,
    })
    .strict(),
  z
    .object({
      kind: z.literal('dispute'),
      reference: z.string().min(1).max(500),
      explanation: boundedText,
    })
    .strict(),
  z
    .object({
      kind: z.literal('payment_proposal'),
      reference: z.string().min(1).max(500),
      amountMinor: positiveMoneyMinorStringSchema,
      currency: z.enum(['PEN', 'USD']),
      date: z.iso.date(),
      explanation: boundedText,
    })
    .strict(),
  z.object({ kind: z.literal('handoff'), reason: boundedText }).strict(),
]);
export type BusinessAction = z.infer<typeof businessActionSchema>;
export const requiresCustomerConfirmation = (
  action: Pick<BusinessAction, 'kind'>
): boolean =>
  [
    'create_lead',
    'book_appointment',
    'request_sales_appointment',
    'cancel_appointment',
    'reschedule_appointment',
    'payment_proposal',
  ].includes(action.kind);

export const compositionSchema = z
  .object({
    phase: z.enum(['read', 'reply']).optional(),
    text: z.string().trim().max(8_000),
    citedFactIds: z.array(z.string()).max(30),
    actions: z.array(businessActionSchema).max(5),
    buyerContextPatch: buyerContextPatchSchema.nullable().optional(),
    selectedTechniqueId: salesTechniqueIdSchema.nullable().optional(),
    salesTurnPlan: salesTurnPlanSchema.nullable().optional(),
    turnUnderstanding: salesTurnUnderstandingSchema.optional(),
    // The days of the appointment times this reply was written to offer.
    appointmentOffer: appointmentOfferSchema
      .omit({ answeredMessageId: true })
      .nullable()
      .optional(),
  })
  .strict()
  .superRefine((composition, ctx) => {
    if (composition.phase === 'read') {
      if (composition.text || !composition.actions.length)
        ctx.addIssue({
          code: 'custom',
          message: 'A read plan contains actions, not a customer reply.',
        });
    } else if (!composition.text) {
      ctx.addIssue({
        code: 'custom',
        message: 'A customer reply must contain text.',
      });
    }
  });
export type ComposedResponse = z.infer<typeof compositionSchema>;

export interface ConversationMessage {
  id: string;
  role: 'customer' | 'assistant' | 'human';
  text: string;
  occurredAt: string;
}

export interface AuthoritativeActionResult {
  reference: string;
  status: 'accepted' | 'confirmed' | 'pending';
  facts: readonly SourceFact[];
}

export class AssistantError extends Error {
  constructor(
    readonly code: string,
    readonly httpStatus = 400,
    readonly retryable = false,
    readonly retryAfterSeconds?: number | null
  ) {
    super(code);
    this.name = 'AssistantError';
  }
}

export const VIRTUAL_ASSISTANT_DISCLOSURE = {
  'es-PE': 'Asistente virtual',
  'en-US': 'Virtual assistant',
} as const;
